import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from 'config/supabase';
import { Subscription } from 'types';

interface SubscriptionContextType {
  subscription: Subscription | null;
  loading: boolean;
  hasAccess: (feature: 'training' | 'all') => boolean;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      // Use Postgres function to get the most relevant subscription
      // (active subscription OR canceled subscription within period)
      const { data, error } = await supabase
        .rpc('get_active_subscription', { p_user_id: user.id })
        .returns<Subscription>()
        .single();

      if (!error && data) {
        setSubscription(data);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const hasAccess = (feature: 'training' | 'all'): boolean => {
    if (!subscription) {
      return false;
    }

    // Check if subscription is active OR canceled but still within period
    let hasValidSubscription = false;
    if (subscription.status === 'active') {
      hasValidSubscription = true;
    } else if (subscription.status === 'canceled' || subscription.status === 'cancelled') {
      // Allow access if still within current period
      const currentPeriodEnd = new Date(subscription.current_period_end);
      const now = new Date();
      hasValidSubscription = now < currentPeriodEnd;
    }

    if (!hasValidSubscription) {
      return false;
    }

    if (feature === 'training') {
      // Training is now available for all plans
      // Starter: Weekly group coaching
      // Growth: Biweekly 1:1 coaching
      // Pro: Weekly 1:1 coaching
      return true;
    }

    return true;
  };

  return (
    <SubscriptionContext.Provider
      value={{ subscription, loading, hasAccess, refetch: fetchSubscription }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
